/**
 * Bundled demo/seed artwork shipped inside the app build.
 *
 * Several catalogue rows (products and categories) store a bare filename such
 * as `spark-plug.jpg`. Those names refer to these bundled assets — NOT to files
 * served from the site root — so rendering them directly into `<img src>`
 * produces a 404 for `/spark-plug.jpg`. Always resolve through this module.
 *
 * Every seeded demo product owns its own photo: the map below is intentionally
 * one entry per distinct part type so a piston kit never shows a brake disc.
 * When you add a demo listing, add its photo here too (plus the 400px
 * AVIF/WebP siblings in `demo-product-image-sets.ts`).
 */
import brakeDisc from "@/assets/products/brake-disc.jpg";
import chainSprocket from "@/assets/products/chain-sprocket.jpg";
import engineOil from "@/assets/products/engine-oil.jpg";
import sparkPlug from "@/assets/products/spark-plug.jpg";
import airFilter from "@/assets/products/air-filter.jpg";
import helmet from "@/assets/products/helmet.jpg";
import battery from "@/assets/products/battery.jpg";
import ledHeadlight from "@/assets/products/led-headlight.jpg";
import tyre from "@/assets/products/tyre.jpg";
import brakePad from "@/assets/products/brake-pad.jpg";

// Part-specific photos added so no two demo listings share a picture.
import airFilterCar from "@/assets/products/air-filter-car.jpg";
import airFilterFzs from "@/assets/products/air-filter-fzs.jpg";
import airFilterNs160 from "@/assets/products/air-filter-ns160.jpg";
import airHorn from "@/assets/products/air-horn.jpg";
import batteryBike from "@/assets/products/battery-bike-9ah.jpg";
import batteryCar from "@/assets/products/battery-car-ns40.jpg";
import brakeDiscCar from "@/assets/products/brake-disc-car.jpg";
import brakeFluid from "@/assets/products/brake-fluid.jpg";
import brakePadFzs from "@/assets/products/brake-pad-fzs.jpg";
import brakeShoe from "@/assets/products/brake-shoe.jpg";
import chainLube from "@/assets/products/chain-lube.jpg";
import chainSprocketCbr from "@/assets/products/chain-sprocket-cbr150.jpg";
import clutchCable from "@/assets/products/clutch-cable.jpg";
import clutchPlateKit from "@/assets/products/clutch-plate-kit.jpg";
import crashGuard from "@/assets/products/crash-guard.jpg";
import engineOil4l from "@/assets/products/engine-oil-4l-jug.jpg";
import engineOil1l from "@/assets/products/engine-oil-shell-1l.jpg";
import forkOil from "@/assets/products/fork-oil.jpg";
import helmetVega from "@/assets/products/helmet-vega.jpg";
import hidKit from "@/assets/products/hid-kit.jpg";
import innerTube from "@/assets/products/inner-tube.jpg";
import ledHeadlightH4 from "@/assets/products/led-headlight-h4.jpg";
import ledIndicatorSet from "@/assets/products/led-indicator-set.jpg";
import oilFilterCar from "@/assets/products/oil-filter-car.jpg";
import pistonKit from "@/assets/products/piston-kit.jpg";
import rearShock from "@/assets/products/rear-shock.jpg";
import ridingGloves from "@/assets/products/riding-gloves.jpg";
import sparkPlugBkr6e from "@/assets/products/spark-plug-bkr6e.jpg";
import sparkPlugCpr8ea from "@/assets/products/spark-plug-cpr8ea.jpg";
import tailLightLed from "@/assets/products/tail-light-led.jpg";
import tankPad from "@/assets/products/tank-pad.jpg";
import tyreNylogrip from "@/assets/products/tyre-nylogrip.jpg";
import tyreTimsunRear from "@/assets/products/tyre-timsun-rear.jpg";

export const BUNDLED_IMAGES: Record<string, string> = {
  "brake-disc.jpg": brakeDisc,
  "chain-sprocket.jpg": chainSprocket,
  "engine-oil.jpg": engineOil,
  "spark-plug.jpg": sparkPlug,
  "air-filter.jpg": airFilter,
  "helmet.jpg": helmet,
  "battery.jpg": battery,
  "led-headlight.jpg": ledHeadlight,
  "tyre.jpg": tyre,
  "brake-pad.jpg": brakePad,
  "air-filter-car.jpg": airFilterCar,
  "air-filter-fzs.jpg": airFilterFzs,
  "air-filter-ns160.jpg": airFilterNs160,
  "air-horn.jpg": airHorn,
  "battery-bike-9ah.jpg": batteryBike,
  "battery-car-ns40.jpg": batteryCar,
  "brake-disc-car.jpg": brakeDiscCar,
  "brake-fluid.jpg": brakeFluid,
  "brake-pad-fzs.jpg": brakePadFzs,
  "brake-shoe.jpg": brakeShoe,
  "chain-lube.jpg": chainLube,
  "chain-sprocket-cbr150.jpg": chainSprocketCbr,
  "clutch-cable.jpg": clutchCable,
  "clutch-plate-kit.jpg": clutchPlateKit,
  "crash-guard.jpg": crashGuard,
  "engine-oil-4l-jug.jpg": engineOil4l,
  "engine-oil-shell-1l.jpg": engineOil1l,
  "fork-oil.jpg": forkOil,
  "helmet-vega.jpg": helmetVega,
  "hid-kit.jpg": hidKit,
  "inner-tube.jpg": innerTube,
  "led-headlight-h4.jpg": ledHeadlightH4,
  "led-indicator-set.jpg": ledIndicatorSet,
  "oil-filter-car.jpg": oilFilterCar,
  "piston-kit.jpg": pistonKit,
  "rear-shock.jpg": rearShock,
  "riding-gloves.jpg": ridingGloves,
  "spark-plug-bkr6e.jpg": sparkPlugBkr6e,
  "spark-plug-cpr8ea.jpg": sparkPlugCpr8ea,
  "tail-light-led.jpg": tailLightLed,
  "tank-pad.jpg": tankPad,
  "tyre-nylogrip.jpg": tyreNylogrip,
  "tyre-timsun-rear.jpg": tyreTimsunRear,
};

export {
  brakeDisc,
  chainSprocket,
  engineOil,
  sparkPlug,
  airFilter,
  helmet,
  battery,
  ledHeadlight,
  tyre,
  brakePad,
};

/**
 * Resolve any stored image reference to something a browser can actually load.
 *
 * - real URLs (`http(s)://`, `data:`, root-relative `/…`) pass through,
 * - known bundled filenames map to the imported asset,
 * - anything else returns `null` so callers can render their own fallback
 *   (a glyph or "no photo" state) instead of a broken image request.
 */
export function resolveBundledImage(name: string | null | undefined): string | null {
  if (typeof name !== "string" || !name) return null;
  if (
    name.startsWith("http") ||
    name.startsWith("data:") ||
    name.startsWith("blob:") ||
    name.startsWith("/")
  ) {
    return name;
  }
  return BUNDLED_IMAGES[name] ?? null;
}
